import type { BridgeInboundMessage, BridgeOutboundMessage } from './bridgeTypes';
import type { SheetApiHost } from './SheetApiHost';
import { getBridgeClientInfo } from './bridgeClientInfo';

const DEFAULT_PORT = 9374;
const RECONNECT_DELAYS_MS = [3_000, 10_000];

let started = false;
let activeStop: (() => void) | null = null;

function bridgeLog(message: string): void {
  if (typeof process !== 'undefined' && process.env?.NODE_ENV === 'test') {
    return;
  }
  if (typeof localStorage !== 'undefined') {
    try {
      if (localStorage.getItem('pouches-bridge-debug') === '1') {
        console.debug(`[BridgeClient] ${message}`);
      }
    } catch {
      // ignore
    }
  }
}

function getBridgeWsUrl(): string {
  const port =
    typeof process !== 'undefined' && process.env?.REACT_APP_POUCHES_CONNECTOR_PORT
      ? Number.parseInt(process.env.REACT_APP_POUCHES_CONNECTOR_PORT, 10)
      : DEFAULT_PORT;
  return `ws://127.0.0.1:${port}/client`;
}

function isOutboundMessage(value: unknown): value is BridgeOutboundMessage {
  if (typeof value !== 'object' || value === null) return false;
  const msg = value as Record<string, unknown>;
  return (
    typeof msg.id === 'string' &&
    typeof msg.method === 'string' &&
    typeof msg.path === 'string'
  );
}

export function startBridgeClient(host: SheetApiHost): () => void {
  if (started && activeStop) {
    return activeStop;
  }
  started = true;

  let ws: WebSocket | null = null;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let reconnectAttempt = 0;
  let disposed = false;
  let connectedOnce = false;

  const clearReconnectTimer = () => {
    if (reconnectTimer !== null) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
  };

  const scheduleReconnect = () => {
    if (disposed) return;
    clearReconnectTimer();
    const delay =
      RECONNECT_DELAYS_MS[
        Math.min(reconnectAttempt, RECONNECT_DELAYS_MS.length - 1)
      ];
    reconnectAttempt += 1;
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null;
      connect();
    }, delay);
  };

  const handleMessage = (event: MessageEvent) => {
    void (async () => {
      let parsed: unknown;
      try {
        parsed = JSON.parse(String(event.data));
      } catch {
        return;
      }
      if (!isOutboundMessage(parsed)) {
        return;
      }

      let response: BridgeInboundMessage;
      try {
        const result = await host.handleRequest(
          parsed.method,
          parsed.path,
          parsed.body,
        );
        response = {
          id: parsed.id,
          status: result.status,
          body: result.body,
        };
      } catch (err) {
        response = {
          id: parsed.id,
          status: 500,
          body: {
            ok: false,
            code: 'INTERNAL_ERROR',
            message: err instanceof Error ? err.message : String(err),
          },
        };
      }

      if (ws?.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify(response));
      }
    })();
  };

  const connect = () => {
    if (disposed) return;
    if (ws && (ws.readyState === WebSocket.CONNECTING || ws.readyState === WebSocket.OPEN)) {
      return;
    }

    try {
      ws = new WebSocket(getBridgeWsUrl());
    } catch {
      scheduleReconnect();
      return;
    }

    ws.addEventListener('open', () => {
      reconnectAttempt = 0;
      const info = getBridgeClientInfo();
      if (ws?.readyState === WebSocket.OPEN) {
        ws.send(
          JSON.stringify({
            type: 'hello',
            kind: info.kind,
            origin: info.origin,
            apiVersion: info.apiVersion,
            capabilities: info.capabilities,
          }),
        );
      }
      if (!connectedOnce) {
        connectedOnce = true;
        bridgeLog(`connected to connector bridge (${info.kind} @ ${info.origin})`);
      }
    });

    ws.addEventListener('message', handleMessage);

    ws.addEventListener('close', () => {
      ws = null;
      scheduleReconnect();
    });

    ws.addEventListener('error', () => {
      // close handler performs reconnect; avoid console noise
    });
  };

  connect();

  const stop = () => {
    disposed = true;
    started = false;
    activeStop = null;
    clearReconnectTimer();
    if (ws) {
      ws.close();
      ws = null;
    }
  };

  activeStop = stop;
  return stop;
}
